import { IconButton } from "@chakra-ui/react"
import {
  EDITOR_LAYERING,
  useEditorBlock,
  useEditorDispatch,
  useEditorUI
} from "@pathwright/blocks-engine"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import BlockSelector from "./BlockSelector"
import PasteBlockButton from "./PasteBlockButton"

const iconSize = 30

const AddBlocksContainer = styled.div`
  position: relative;
  left: 0px;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #ccc;
  min-height: 1px;
  top: ${(p) => (p.top ? `${0}px` : "auto")};
  bottom: ${(p) => (p.top ? "auto" : `-${1}px`)};
  opacity: ${(p) => (p.visible ? 1 : 0)};

  &:hover {
    opacity: 1;
  }

  &.AddBlocks--open .AddBlocks__Icon {
    z-index: ${EDITOR_LAYERING.OPEN_BLOCK_SELECTOR_ADD_BLOCK};
  }
`

const AddBlocksWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${(p) => (p.forceOpen ? "column" : "row")};

  &:hover {
    opacity: 1;
  }

  width: 100%;
  transition: all 0.3s;
  height: 0px;
  left: 0px;

  &.AddBlocks--open {
    z-index: ${EDITOR_LAYERING.OPEN_BLOCK_SELECTOR};
    opacity: 1;
    height: ${(p) =>
      p.copiedBlock ? (p.forceOpen ? "242px" : "272px") : "236px"};
    overflow: ${(p) => (p.forceOpen && p.copiedBlock ? "visible" : "hidden")};
  }

  ${(p) =>
    p.forceOpen
      ? `
    & .PasteButton {
      margin-bottom: -19px;
      z-index: 1;

      .PasteButton__inner-wrapper {
        border: 1px solid rgba(150,150,150,0.2);
      }
    }
  `
      : ``}
`

const AddIconWrap = styled.div`
  position: absolute;
  top: ${-iconSize / 2 - 1}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0px;

  height: ${iconSize}px;
  width: ${iconSize}px;
  border-radius: 100%;
  background-color: #efefef;
  pointer-events: all;
  transform: rotate(${(p) => (p.open ? 45 : 0)}deg);
  transition: all 0.3s;
`

const AddBlocks = ({
  blockIndex,
  onAddBlock,
  forceOpen,
  style,
  top,
  onCancelPaste,
  isLastBlock
}) => {
  const [open, setOpen] = useState(!!forceOpen)
  const [visible, setVisible] = useState(false)
  const addBlockRef = useRef(null)
  const { copiedBlockClip: copiedBlock, pastingBlockIndex } = useEditorBlock()
  const { handlePasteBlock: onPasteBlock } = useEditorDispatch()
  const { triggeredAddBlock, setTriggeredAddBlock } = useEditorUI()

  // Close the block selector after copying a block to the clipboard
  useEffect(() => {
    if (copiedBlock) {
      setOpen(false)
    }
  }, [copiedBlock])

  // Allows opening the block selector externally (ex: keyboard shortcut)
  useEffect(() => {
    const triggerAsLastBlock = triggeredAddBlock === -1 && isLastBlock
    const triggerAsBlockIndex = triggeredAddBlock === blockIndex
    if ((triggerAsLastBlock || triggerAsBlockIndex) && !top) {
      toggleOpen()
      setTriggeredAddBlock(null)
    }
  }, [triggeredAddBlock])

  useEffect(() => {
    if (open) {
      addBlockRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      })
      addBlockRef.current.focus()
    }
  }, [open])

  const toggleOpen = () => setOpen(!open)

  const handleAddBlock = (type, data, layout) => {
    onAddBlock(type, data, layout)
    setVisible(false)
    setOpen(false)
  }

  return (
    <AddBlocksContainer
      className={`AddBlocks${!copiedBlock && open ? ` AddBlocks--open` : ""}`}
      style={style}
      top={top}
      ref={addBlockRef}
      tabIndex="0"
      visible={visible || open}
    >
      <AddBlocksWrap
        open={!copiedBlock && open}
        className={
          (!copiedBlock && open) || forceOpen
            ? "AddBlocks--open"
            : "AddBlocks--closed"
        }
        copiedBlock={copiedBlock}
        forceOpen={forceOpen}
        tabIndex="0"
      >
        {forceOpen ? (
          <PasteBlockButton
            index={blockIndex}
            copiedBlock={copiedBlock}
            onPaste={onPasteBlock}
            onCancel={onCancelPaste}
            pasting={pastingBlockIndex === blockIndex}
          />
        ) : null}
        {(!copiedBlock && open) || forceOpen ? (
          <BlockSelector onSelect={handleAddBlock} onClose={toggleOpen} />
        ) : null}
      </AddBlocksWrap>
      {!forceOpen && (
        <AddIconWrap
          className="AddBlocks__Icon"
          onClick={toggleOpen}
          open={!copiedBlock && open}
        >
          {copiedBlock ? (
            <PasteBlockButton
              index={blockIndex}
              copiedBlock={copiedBlock}
              onPaste={onPasteBlock}
              onCancel={onCancelPaste}
              pasting={pastingBlockIndex === blockIndex}
            />
          ) : (
            <IconButton
              minW="0"
              color={"#888687"}
              cursor="pointer"
              icon={<Pathicon icon="plus" />}
            />
          )}
        </AddIconWrap>
      )}
    </AddBlocksContainer>
  )
}

export default AddBlocks
